import {
	CustomerSalesPerformanceDto,
	ItemSalesPerformanceDto,
	SAL2050_R01Dto,
	SAL2050_R01SubtotalRow,
	SAL2050_R01_SUBTOTAL_TYPES,
	SalesPerformanceAmount,
} from '../Dto_SAL2050_R01';
import { SAL2050_R01TabType } from '../Store_SAL2050_R01';

export type SAL2050_R01GridRowData = SAL2050_R01Dto | SAL2050_R01SubtotalRow;

export const SAL2050_R01isSubtotalRow = (row: SAL2050_R01GridRowData): row is SAL2050_R01SubtotalRow => {
	return 'isSubtotal' in row && row.isSubtotal === true;
};

export const SAL2050_R01isCustomerSubtotalRow = (row: SAL2050_R01GridRowData): row is SAL2050_R01SubtotalRow => {
	return SAL2050_R01isSubtotalRow(row) && row.subtotalType === SAL2050_R01_SUBTOTAL_TYPES.CUSTOMER;
};

export const SAL2050_R01isItemSubtotalRow = (row: SAL2050_R01GridRowData): row is SAL2050_R01SubtotalRow => {
	return SAL2050_R01isSubtotalRow(row) && row.subtotalType === SAL2050_R01_SUBTOTAL_TYPES.ITEM;
};

export const isSubtotalRow = SAL2050_R01isSubtotalRow;
export const isCustomerSubtotalRow = SAL2050_R01isCustomerSubtotalRow;
export const isItemSubtotalRow = SAL2050_R01isItemSubtotalRow;

const calcDiffRate = (prevYearSalesAmt: number, currYearSalesAmt: number): number => {
	if (prevYearSalesAmt === 0) return currYearSalesAmt === 0 ? 0 : 100;
	return ((currYearSalesAmt - prevYearSalesAmt) / prevYearSalesAmt) * 100;
};

const createEmptySubtotal = (
	item: CustomerSalesPerformanceDto | ItemSalesPerformanceDto,
	subtotalType: typeof SAL2050_R01_SUBTOTAL_TYPES[keyof typeof SAL2050_R01_SUBTOTAL_TYPES],
	groupKey: string,
	groupLabel: string,
): SAL2050_R01SubtotalRow => ({
	isSubtotal: true,
	subtotalType,
	groupKey,
	groupLabel,
	count: 0,
	compCd: item.compCd,
	compNm: item.compNm,
	customCd: 'customCd' in item ? item.customCd : '',
	customNm: 'customNm' in item ? item.customNm : '',
	itemCd: 'itemCd' in item ? item.itemCd : '',
	itemNm: 'itemNm' in item ? item.itemNm : '',
	itemSpec: 'itemSpec' in item ? item.itemSpec : '',
	remark: item.remark,
	prevYearSalesAmt: 0,
	currYearSalesAmt: 0,
	diffAmt: 0,
	diffRate: 0,
	valueIndex: item.valueIndex,
	orderIndex: item.orderIndex,
	orderIndexSeq: item.orderIndexSeq,
	inserted: false,
	isModified: false,
});

const accumulateAmount = (target: SAL2050_R01SubtotalRow, item: SalesPerformanceAmount) => {
	target.count += 1;
	target.prevYearSalesAmt += Number(item.prevYearSalesAmt) || 0;
	target.currYearSalesAmt += Number(item.currYearSalesAmt) || 0;
	target.diffAmt += Number(item.diffAmt) || 0;
	target.diffRate = calcDiffRate(target.prevYearSalesAmt, target.currYearSalesAmt);
};

export const calculateCustomerSubtotals = (data: CustomerSalesPerformanceDto[]): SAL2050_R01SubtotalRow[] => {
	if (!data || data.length === 0) return [];

	const grouped = data.reduce((acc, item) => {
		const key = item.customCd;
		if (!key) return acc;

		if (!acc[key]) {
			acc[key] = createEmptySubtotal(
				item,
				SAL2050_R01_SUBTOTAL_TYPES.CUSTOMER,
				key,
				`${item.customNm || key} 소계`,
			);
		}
		accumulateAmount(acc[key], item);
		return acc;
	}, {} as Record<string, SAL2050_R01SubtotalRow>);

	return Object.values(grouped).sort((a, b) => a.groupKey.localeCompare(b.groupKey));
};

export const calculateItemSubtotals = (data: ItemSalesPerformanceDto[]): SAL2050_R01SubtotalRow[] => {
	if (!data || data.length === 0) return [];

	const grouped = data.reduce((acc, item) => {
		const key = item.itemCd;
		if (!key) return acc;

		if (!acc[key]) {
			acc[key] = createEmptySubtotal(
				item,
				SAL2050_R01_SUBTOTAL_TYPES.ITEM,
				key,
				`${item.itemNm || key} 소계`,
			);
		}
		accumulateAmount(acc[key], item);
		return acc;
	}, {} as Record<string, SAL2050_R01SubtotalRow>);

	return Object.values(grouped).sort((a, b) => a.groupKey.localeCompare(b.groupKey));
};

export const getSubtotalCalculator = (tabType: SAL2050_R01TabType) => {
	switch (tabType) {
		case 'customer':
			return calculateCustomerSubtotals;
		case 'item':
			return calculateItemSubtotals;
		default:
			return () => [];
	}
};

export const mergeDataWithSubtotals = <T extends SAL2050_R01Dto>(
	data: T[],
	subtotals: SAL2050_R01SubtotalRow[],
	showSubtotals: boolean,
	tabType?: SAL2050_R01TabType
): SAL2050_R01GridRowData[] => {
	if (!showSubtotals || subtotals.length === 0) {
		return data as SAL2050_R01GridRowData[];
	}

	const result: SAL2050_R01GridRowData[] = [];
	const subtotalMap = new Map(subtotals.map(s => [s.groupKey, s]));

	const getGroupKey = (item: any, currentTab?: SAL2050_R01TabType) => {
		if (currentTab === 'customer' && item.customCd) return item.customCd;
		if (currentTab === 'item' && item.itemCd) return item.itemCd;
		if (item.customCd) return item.customCd;
		if (item.itemCd) return item.itemCd;
		return '';
	};

	const groupedData = data.reduce((acc, item) => {
		const groupKey = getGroupKey(item, tabType);
		if (!groupKey) return acc;
		if (!acc[groupKey]) acc[groupKey] = [];
		acc[groupKey].push(item);
		return acc;
	}, {} as Record<string, T[]>);

	Object.keys(groupedData).sort().forEach(groupKey => {
		const groupData = groupedData[groupKey];
		const subtotal = subtotalMap.get(groupKey);

		result.push(...(groupData as SAL2050_R01GridRowData[]));

		if (subtotal) {
			result.push(subtotal);
		}
	});

	return result;
};
